import { createContext, type ComponentChildren } from "preact";
import { useContext, useLayoutEffect, useMemo } from "preact/hooks";
import { useTranslation } from "react-i18next";

import { analytics } from "./analyticsManager";
import type { AnalyticsActionData, AnalyticsIntent, Module } from "./types";
import { useSDKContext } from "../sdk";

type AnalyticsContextType = {
  intent?: AnalyticsIntent;
  actionData?: AnalyticsActionData;
  module?: Module;
  selectedMethod?: PaymentMethodSlugs;
  analytics: typeof analytics;
};

const AnalyticsContext = createContext<AnalyticsContextType>(
  {} as AnalyticsContextType
);

type AnalyticsProviderProps = Omit<AnalyticsContextType, "analytics"> & {
  children: ComponentChildren;
};

export default function AnalyticsProvider({
  intent,
  actionData,
  module,
  selectedMethod,
  children,
}: AnalyticsProviderProps) {
  const { i18n } = useTranslation();
  const { isEmbedBySDK } = useSDKContext();

  // register meta data before any useEffect runs for page views
  useLayoutEffect(() => {
    if (!intent || !actionData) return;

    if (intent.tracking_id) analytics.identify(intent.tracking_id);
    analytics.registerMetaData({
      intent,
      actionData,
      language: i18n.language,
      isEmbedBySDK,
    });
  }, [intent, actionData, i18n, isEmbedBySDK]);

  const contextValue = useMemo(
    () => ({ intent, actionData, module, selectedMethod, analytics }),
    [actionData, intent, module, selectedMethod]
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    console.warn("useAnalytics must be used inside <AnalyticsProvider />");
  }

  return context;
};
